/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2025-07-07.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "pfia-2025-dijon-recherche-ia-francophone",
  "title": "PFIA 2025 à Dijon : la recherche francophone en IA vue depuis Besançon",
  "excerpt": "Du 30 juin au 4 juillet, l’Université Bourgogne Europe a accueilli à Dijon neuf conférences de recherche en IA, plus de 500 participants annoncés et une journée dédiée à l’écosystème régional dont le programme publié tenait en une matinée.",
  "seoTitle": "PFIA 2025 à Dijon : neuf conférences en IA",
  "seoDescription": "Du 30 juin au 4 juillet 2025, la 18e PFIA a réuni à Dijon neuf conférences de recherche en IA et plus de 500 participants annoncés, à une heure de Besançon.",
  "category": "recherche",
  "tags": [
    "PFIA",
    "Université Bourgogne Europe",
    "DEDIHCATED",
    "FEMTO-ST",
    "Hors Besançon"
  ],
  "author": "jimmy",
  "publishedAt": "2025-07-07",
  "updatedAt": "2025-07-07",
  "featured": false,
  "cover": {
    "image": "/img/blog/amphitheatre-polytech-batiment-mirande-dijon-fr21-2025-07-02.jpg",
    "credit": "Photo : Amphithéâtre Polytech du bâtiment Mirande, campus de Dijon — Chabe01, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Amphithéâtre du campus de l'Université Bourgogne Europe à Dijon pendant une conférence scientifique : vue depuis les gradins du fond, public assis devant un écran de présentation, logo Polytech Dijon au mur"
  },
  "content": [
    {
      "type": "lead",
      "text": "Du 30 juin au 4 juillet, l’Université Bourgogne Europe a accueilli à Dijon la 18e Plate-Forme Intelligence Artificielle (PFIA), le rassemblement annuel de la recherche francophone en IA. Neuf conférences scientifiques, plus de 500 participants annoncés par le livret du participant, une journée régionale dont le programme publié tenait en une matinée. Pour les équipes bisontines, l’événement se tenait à une heure de train : l’occasion d’observer comment se répartit la recherche en IA entre les deux pôles de la région."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "18e Plate-Forme Intelligence Artificielle (PFIA 2025)"
        },
        {
          "label": "Quand",
          "value": "Du lundi 30 juin au vendredi 4 juillet 2025"
        },
        {
          "label": "Où",
          "value": "Campus de l’Université Bourgogne Europe à Dijon, bâtiments Mirande et Polytech"
        },
        {
          "label": "Qui",
          "value": "Université Bourgogne Europe et le laboratoire LIB, avec l’AFIA"
        },
        {
          "label": "Pour qui",
          "value": "Chercheurs, doctorants, industriels de la communauté francophone en IA"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Neuf conférences réunies sous un même toit"
    },
    {
      "type": "p",
      "text": "La PFIA n’est pas une conférence, mais une constellation. Le principe, biennal jusqu’en 2017 puis annuel[^3], consiste à faire converger au même endroit et la même semaine les rendez-vous des différentes sous-disciplines de l’intelligence artificielle. L’édition 2025 était organisée par l’Université Bourgogne Europe et le Laboratoire d’informatique de Bourgogne (LIB), avec l’Association française pour l’intelligence artificielle (AFIA)[^4]."
    },
    {
      "type": "p",
      "text": "Le livret du participant donne la mesure de l’ensemble : neuf conférences, trois journées thématiques auxquelles s’ajoute une journée « IA en Bourgogne-Franche-Comté », cinq ateliers et six tutoriels, pour **plus de 500 participants et 250 présentations orales annoncés, dont 10 exposés invités**[^3]. Ce sont les chiffres annoncés par les organisateurs dans ce livret, non un bilan de fréquentation. Les conférences hébergées couvraient un spectre large :"
    },
    {
      "type": "ul",
      "items": [
        "**CNIA**, 28e Conférence nationale d’intelligence artificielle, et **RJCIA**, rencontres des jeunes chercheurs[^3] ;",
        "**CAp**, 27e conférence sur l’apprentissage automatique, et **SFC**, 30es rencontres de la Société francophone de classification[^3] ;",
        "**JFPC** (programmation par contraintes), **JIAF** (IA fondamentale) et **JFSMA** (systèmes multi-agents)[^3] ;",
        "**IC**, journées d’ingénierie des connaissances, et **APIA**, consacrée aux applications pratiques de l’IA[^3]."
      ]
    },
    {
      "type": "p",
      "text": "Les exposés invités venaient d’Inria, du CNRS, de Polytechnique Montréal ou du Kempner Institute de Harvard, et tout se déroulait entre les bâtiments Mirande et Polytech du campus dijonnais[^3]."
    },
    {
      "type": "h2",
      "text": "Le 2 juillet, la journée « IA en BFC »"
    },
    {
      "type": "p",
      "text": "Le mercredi 2 juillet, la manifestation a ouvert ses portes au tissu économique régional : une journée co-organisée par l’Université Bourgogne Europe et la SATT [Sayens](https://www.sayens.fr/evenements/entreprises/evenement-ia-en-bfc/), dans le bâtiment Mirande, entrée gratuite sur inscription[^2] — mais dont le programme publié s’arrêtait en fin de matinée."
    },
    {
      "type": "p",
      "text": "La première séquence portait sur la stratégie de l’établissement d’accueil : introduction du professeur Laurent Gautier, vice-président délégué à la valorisation de la recherche, présentation du Pôle IA interdisciplinaire par Fabrice Meriaudeau, puis du hub européen DEDIHCATED par Christophe Nicolle[^1]. La séquence suivante, animée par Matthieu Bach (Sayens), donnait la parole à trois entreprises : ATOL CD sur l’IA en entreprise, SKAIZen avec Romain Alfred sur « la modélisation du risque géopolitique par l’IA prédictive », et Syartec avec Eric Lebon, témoignage sur l’innovation partenariale et l’IA dans le domaine automobile[^1]. La matinée s’achevait par un temps de réseautage[^1]."
    },
    {
      "type": "p",
      "text": "Ces entreprises ne se sont pas limitées à la vitrine régionale : SKAIZen Group et Syartec figuraient aussi au programme de la conférence APIA, respectivement avec « GDS : un réseau de neurones antagoniste générateur de messages SWIFT » et une communication sur l’analyse de données pour la décision dans le commerce automobile[^3]. Passer du statut d’auditeur à celui de contributeur : c’est l’objet de ce type de journée."
    },
    {
      "type": "h2",
      "text": "DEDIHCATED, un dispositif piloté depuis le Doubs"
    },
    {
      "type": "p",
      "text": "DEDIHCATED BFC, présenté à Dijon, déplace le centre de gravité. Ce pôle européen d’innovation numérique (EDIH), labellisé par la Commission européenne, a pour coordinateur l’association Pôle Véhicule du Futur, basée à Étupes, dans le Doubs[^5]. Autour de ce coordinateur, la fiche européenne recense treize partenaires, parmi lesquels l’UTBM et l’Université de Franche-Comté — devenue [Université Marie et Louis Pasteur](/blog/se-former-ia-besancon-guide-voies-acces) au 1er janvier 2025 —, et présente le dispositif comme un guichet unique de la transition numérique pour les entreprises industrielles[^5]."
    },
    {
      "type": "p",
      "text": "Il propose trois parcours thématiques — données et IA pour la gestion d’entreprise, numérisation des processus jusqu’aux jumeaux numériques, formation aux compétences métiers —, auxquels s’ajoute un volet cybersécurité[^5]. Les secteurs visés — automobile, mécanique, mécatronique, agroalimentaire, énergie — recouvrent une bonne part du socle industriel du nord Franche-Comté et du bassin bisontin[^5]."
    },
    {
      "type": "h2",
      "text": "Un axe Dijon–Besançon encore asymétrique"
    },
    {
      "type": "p",
      "text": "Le programme publié de la journée « IA en BFC » ne fait apparaître aucun laboratoire bisontin parmi les intervenants annoncés, et le livret du participant ne mentionne ni [FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques) ni l’Université Marie et Louis Pasteur[^1][^3]. La journée régionale a surtout servi de vitrine à l’écosystème dijonnais, ce qui est logique pour un établissement organisateur, mais laisse une part du territoire hors champ."
    },
    {
      "type": "p",
      "text": "Cette absence ne dit rien des compétences bisontines : Besançon dispose de son propre format. Le 10 avril, la Maison de l’économie accueillait la deuxième édition du [Printemps de l’IA](/blog/printemps-ia-2026-besancon-cci-industrie), organisée par le Pôle des Microtechniques et l’institut FEMTO-ST : environ 200 participants autour des usages de l’IA dans les PME industrielles, avec des entreprises comme Hivelix ou Livdeo[^6]. Deux rendez-vous, deux publics : la recherche académique d’un côté, le transfert vers les PME de l’autre."
    },
    {
      "type": "callout",
      "tone": "info",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "La PFIA est itinérante : La Rochelle en 2024, Dijon en 2025[^3]. Une nouvelle édition aussi proche de Besançon n’a rien d’acquis. Cette proximité offrait aux doctorants et aux équipes bisontines un accès rare à toute la communauté francophone — CNIA, CAp, JFSMA, ingénierie des connaissances — sans budget de déplacement. La question, pour les prochaines éditions, est moins celle de la présence que celle de la visibilité : figurer au programme, et pas seulement dans la salle."
    },
    {
      "type": "h2",
      "text": "Une carte régionale à compléter"
    },
    {
      "type": "p",
      "text": "PFIA 2025 aura montré que la Bourgogne-Franche-Comté sait accueillir un événement de cette ampleur, et que ses entreprises ont des travaux à y présenter. Elle aura aussi rappelé que l’IA régionale se raconte en deux récits parallèles, celui de Dijon et celui de Besançon, alors que les dispositifs de financement, DEDIHCATED en tête, sont construits à l’échelle des deux anciennes régions[^5]. Reste à voir si le Printemps de l’IA, dont la deuxième édition s’est tenue à Besançon en avril[^6], sera reconduit — et si la prochaine PFIA verra des équipes bisontines au programme."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "IA en BFC — PFIA 2025 (programme de la journée)",
      "publisher": "Université Bourgogne Europe",
      "url": "https://pfia2025.u-bourgogne.fr/journees/IA%20en%20BFC/",
      "type": "official",
      "date": ""
    },
    {
      "id": 2,
      "title": "Événement IA en Bourgogne-Franche-Comté",
      "publisher": "SATT Sayens",
      "url": "https://www.sayens.fr/evenements/entreprises/evenement-ia-en-bfc/",
      "type": "official",
      "date": ""
    },
    {
      "id": 3,
      "title": "Livret du participant — Plate-Forme IA 2025, Dijon, 30 juin au 4 juillet 2025",
      "publisher": "AFIA, LIB, Université Bourgogne Europe",
      "url": "https://pfia2025.u-bourgogne.fr/programme/Livret_PFIA_2025.pdf",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Plate-Forme Intelligence Artificielle : la 18e édition du PFIA aura lieu à Dijon !",
      "publisher": "Laboratoire d’informatique de Bourgogne (LIB)",
      "url": "https://lib.ube.fr/plate-forme-intelligence-artificielle-la-18eme-edition-du-pfia-aura-lieu-a-dijon/",
      "type": "official",
      "date": ""
    },
    {
      "id": 5,
      "title": "DEDIHCATED BFC — fiche du réseau European Digital Innovation Hubs",
      "publisher": "Commission européenne",
      "url": "https://european-digital-innovation-hubs.ec.europa.eu/edih-catalogue/dedihcated-bfc",
      "type": "official",
      "date": ""
    },
    {
      "id": 6,
      "title": "Au Printemps de l’IA de Besançon, des « offreurs de solutions » mettent l’intelligence artificielle au service des PME industrielles",
      "publisher": "Traces Écrites News",
      "url": "https://www.tracesecritesnews.fr/actualite/au-printemps-de-l-ia-de-besancon-des-offreurs-de-solutions-mettent-l-intelligence-artificielle-au-service-des-pme-industrielles-222774",
      "type": "press",
      "date": "2025-04-15"
    }
  ]
};
